import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../App';

const TABS = [
  { key: 'card', label: 'Battle Card', icon: 'fa-shield-halved' },
  { key: 'objection', label: 'Objection Handler', icon: 'fa-comments' },
  { key: 'followup', label: 'Follow-up Writer', icon: 'fa-pen-nib' },
  { key: 'debrief', label: 'Call Debrief', icon: 'fa-microphone-lines' },
  { key: 'coaching', label: 'Rep Coaching', icon: 'fa-dumbbell' },
];

const Panel = ({ title, icon, children, testid }) => (
  <div className="p-5 rounded-2xl border border-border bg-surface" data-testid={testid}>
    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-text-muted mb-3">
      <i className={`fa-solid ${icon} mr-2 text-primary`} />{title}
    </p>
    {children}
  </div>
);

export default function CloseCopilot() {
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get('tab') || 'card');
  const [leads, setLeads] = useState([]);
  const [leadId, setLeadId] = useState(params.get('lead') || '');
  const [card, setCard] = useState(null);
  const [cardLoading, setCardLoading] = useState(false);

  const [objection, setObjection] = useState('');
  const [objResult, setObjResult] = useState(null);
  const [objLoading, setObjLoading] = useState(false);

  const [fuChannel, setFuChannel] = useState('email');
  const [fuContext, setFuContext] = useState('');
  const [fuResult, setFuResult] = useState(null);
  const [fuLoading, setFuLoading] = useState(false);

  const [transcript, setTranscript] = useState('');
  const [applyStatus, setApplyStatus] = useState(true);
  const [debrief, setDebrief] = useState(null);
  const [debriefLoading, setDebriefLoading] = useState(false);

  const [coach, setCoach] = useState(null);
  const [board, setBoard] = useState([]);
  const [coachLoading, setCoachLoading] = useState(false);

  useEffect(() => {
    api.get('/api/leads', { params: { limit: 100 } })
      .then(r => {
        const list = r.data.leads || r.data || [];
        setLeads(list);
        setLeadId(prev => prev || (list[0]?.id || ''));
      })
      .catch(() => toast.error('Could not load leads'));
  }, []);

  const selectTab = (k) => { setTab(k); setParams({ tab: k, ...(leadId ? { lead: leadId } : {}) }); };

  const lead = leads.find(l => l.id === leadId);

  const loadCard = useCallback(async (refresh = false) => {
    if (!leadId) return;
    setCardLoading(true);
    try {
      const res = await api.get(`/api/closer/battle-card/${leadId}`, { params: refresh ? { refresh: true } : {} });
      setCard(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Battle card failed');
    } finally {
      setCardLoading(false);
    }
  }, [leadId]);

  useEffect(() => { if (tab === 'card' && leadId) loadCard(false); }, [tab, leadId, loadCard]);

  const loadCoaching = useCallback(async (refresh = false) => {
    setCoachLoading(true);
    try {
      const [c, b] = await Promise.all([
        api.get('/api/closer/coaching', { params: refresh ? { refresh: true } : {} }),
        api.get('/api/closer/coaching-board').catch(() => ({ data: { reps: [] } })),
      ]);
      setCoach(c.data);
      setBoard(b.data.reps || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Coaching failed');
    } finally {
      setCoachLoading(false);
    }
  }, []);

  useEffect(() => { if (tab === 'coaching' && !coach) loadCoaching(false); }, [tab, coach, loadCoaching]);

  const askObjection = async () => {
    if (!objection.trim()) { toast.error('Type what they said'); return; }
    setObjLoading(true);
    try {
      const res = await api.post('/api/closer/objection', { objection, lead_id: leadId || undefined });
      setObjResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not generate a response');
    } finally {
      setObjLoading(false);
    }
  };

  const writeFollowUp = async () => {
    if (!leadId) { toast.error('Pick a lead first'); return; }
    setFuLoading(true);
    try {
      const res = await api.post('/api/closer/follow-up', { lead_id: leadId, channel: fuChannel, context: fuContext });
      setFuResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Draft failed');
    } finally {
      setFuLoading(false);
    }
  };

  const runDebrief = async () => {
    if (transcript.trim().length < 20) { toast.error('Paste the call notes first'); return; }
    setDebriefLoading(true);
    try {
      const res = await api.post('/api/closer/debrief', { lead_id: leadId || undefined, transcript, apply_status: applyStatus });
      setDebrief(res.data);
      toast.success('Debrief saved to the lead timeline');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Debrief failed');
    } finally {
      setDebriefLoading(false);
    }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success('Copied'); };
  const k = card?.card || {};

  return (
    <div className="space-y-6" data-testid="closer-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-text-muted mb-1">Sales</p>
          <h1 className="text-3xl font-black tracking-tight text-text-primary">AI Close Copilot</h1>
          <p className="text-sm text-text-muted mt-1 max-w-2xl">
            Everything a rep needs in the 60 seconds before they dial and the 5 minutes after they hang up:
            the battle card for this exact roof, word-for-word objection answers, follow-ups that get replies,
            and a manager-grade debrief of every call.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1.5">Working lead</label>
            <select value={leadId} onChange={e => setLeadId(e.target.value)} className="input-field w-72" data-testid="closer-lead-select">
              <option value="">— no lead —</option>
              {leads.map(l => (
                <option key={l.id} value={l.id}>
                  {(l.owner_name || l.name || l.address || 'Lead')} · {l.city || ''} {l.score ? `(${l.score})` : ''}
                </option>
              ))}
            </select>
          </div>
          {lead && (
            <Link to={`/leads/${lead.id}`} className="px-4 py-2.5 rounded-xl bg-surface border border-border text-sm font-bold hover:border-primary" data-testid="closer-open-lead">
              <i className="fa-solid fa-up-right-from-square mr-2" />Open lead
            </Link>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="closer-tabs">
        {TABS.map(t => (
          <button key={t.key} onClick={() => selectTab(t.key)}
            className={`px-4 py-2 rounded-xl text-sm font-bold border transition-all ${tab === t.key ? 'border-primary bg-surface shadow-sm text-text-primary' : 'border-border bg-surface-alt text-text-muted hover:border-primary/40'}`}
            data-testid={`closer-tab-${t.key}`}>
            <i className={`fa-solid ${t.icon} mr-2`} />{t.label}
          </button>
        ))}
      </div>

      {tab === 'card' && (
        <div className="space-y-4" data-testid="closer-battle-card">
          <div className="flex items-center justify-between gap-3">
            <p className="text-[11px] text-text-muted">
              {card?.generated_at ? `Built ${card.generated_at.slice(5, 16).replace('T', ' ')} UTC${card.cached ? ' (cached)' : ''} · ${k.generated_by || ''}` : ''}
            </p>
            <button onClick={() => loadCard(true)} disabled={cardLoading || !leadId}
              className="px-4 py-2 rounded-xl bg-text-primary text-surface text-sm font-bold disabled:opacity-60" data-testid="closer-rebuild-card">
              {cardLoading ? <><i className="fa-solid fa-spinner fa-spin mr-2" />Building…</> : <><i className="fa-solid fa-rotate mr-2" />Rebuild card</>}
            </button>
          </div>
          {!leadId ? (
            <div className="p-12 text-center text-sm text-text-muted rounded-2xl border border-border bg-surface">Pick a lead to build its battle card.</div>
          ) : cardLoading && !card ? (
            <div className="p-12 text-center text-text-muted rounded-2xl border border-border bg-surface"><i className="fa-solid fa-spinner fa-spin mr-2" />Reading the file…</div>
          ) : card ? (
            <>
              <div className="p-6 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-transparent">
                <h2 className="text-xl font-black text-text-primary" data-testid="closer-card-headline">{k.headline}</h2>
                <p className="text-sm text-text-muted mt-2">{k.why_now}</p>
                <div className="mt-4 px-4 py-3 rounded-xl bg-surface border border-border">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Next best action</p>
                  <p className="text-sm font-bold text-text-primary" data-testid="closer-card-nba">{k.next_best_action}</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Panel title="Talk track" icon="fa-microphone" testid="closer-talk-track">
                  <ol className="space-y-2">
                    {(k.talk_track || []).map((t, i) => (
                      <li key={i} className="flex gap-3 text-sm text-text-primary">
                        <span className="w-5 h-5 shrink-0 rounded-md bg-primary/15 text-primary text-[11px] font-black flex items-center justify-center">{i + 1}</span>
                        <span>{t}</span>
                      </li>
                    ))}
                  </ol>
                </Panel>
                <Panel title="Discovery questions" icon="fa-circle-question" testid="closer-questions">
                  <ul className="space-y-2">
                    {(k.discovery_questions || []).map((q, i) => (
                      <li key={i} className="text-sm text-text-primary flex gap-2"><i className="fa-solid fa-angle-right text-primary mt-1" />{q}</li>
                    ))}
                  </ul>
                </Panel>
                <Panel title="Objections they'll throw" icon="fa-shield" testid="closer-card-objections">
                  <div className="space-y-3">
                    {(k.objections || []).map((o, i) => (
                      <div key={i} className="p-3 rounded-xl bg-surface-alt border border-border">
                        <p className="text-[12px] font-black text-rose-600 mb-1">“{o.objection}”</p>
                        <p className="text-sm text-text-primary">{o.response}</p>
                      </div>
                    ))}
                  </div>
                </Panel>
                <Panel title="Financing to lead with" icon="fa-hand-holding-dollar" testid="closer-financing">
                  <div className="space-y-2">
                    {(k.financing || []).map((f, i) => (
                      <div key={i} className="flex items-start justify-between gap-3 p-3 rounded-xl bg-surface-alt border border-border">
                        <div>
                          <p className="text-sm font-black text-text-primary">{f.option}</p>
                          <p className="text-[11px] text-text-muted">{f.note}</p>
                        </div>
                        {f.monthly ? <p className="text-sm font-black text-emerald-600 whitespace-nowrap">{f.monthly}</p> : null}
                      </div>
                    ))}
                  </div>
                  {(k.risks || []).length > 0 && (
                    <div className="mt-4">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-2">Watch out for</p>
                      <ul className="space-y-1">
                        {k.risks.map((r, i) => <li key={i} className="text-[12px] text-amber-700 flex gap-2"><i className="fa-solid fa-triangle-exclamation mt-0.5" />{r}</li>)}
                      </ul>
                    </div>
                  )}
                </Panel>
              </div>
            </>
          ) : null}
        </div>
      )}

      {tab === 'objection' && (
        <div className="space-y-4" data-testid="closer-objection-tab">
          <Panel title="What did they just say?" icon="fa-comments">
            <div className="flex flex-wrap gap-2 mb-3">
              {['It\'s too expensive', 'I need to talk to my spouse', 'I want to wait', 'I\'m moving in a few years', 'I got a cheaper quote', 'I don\'t trust solar companies'].map(q => (
                <button key={q} onClick={() => setObjection(q)} className="px-3 py-1.5 rounded-lg bg-surface-alt border border-border text-[11px] font-bold text-text-muted hover:border-primary">{q}</button>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={objection} onChange={e => setObjection(e.target.value)} placeholder="Type the objection word for word"
                className="input-field flex-1" data-testid="closer-objection-input" />
              <button onClick={askObjection} disabled={objLoading} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 text-white text-sm font-bold disabled:opacity-60" data-testid="closer-objection-btn">
                {objLoading ? <><i className="fa-solid fa-spinner fa-spin mr-2" />Thinking…</> : <><i className="fa-solid fa-bolt mr-2" />Give me the answer</>}
              </button>
            </div>
          </Panel>
          {objResult && (
            <div className="space-y-3" data-testid="closer-objection-result">
              <div className="px-4 py-3 rounded-xl bg-sky-50 border border-sky-200 text-sm text-sky-900"><b>Read:</b> {objResult.read}</div>
              {(objResult.responses || []).map((r, i) => (
                <div key={i} className="p-4 rounded-2xl border border-border bg-surface">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-primary">{r.style}</p>
                    <button onClick={() => copy(r.say)} className="text-[11px] font-bold text-text-muted hover:text-primary"><i className="fa-solid fa-copy mr-1" />Copy</button>
                  </div>
                  <p className="text-[15px] text-text-primary">“{r.say}”</p>
                </div>
              ))}
              <div className="grid md:grid-cols-3 gap-3">
                <Panel title="Proof point" icon="fa-certificate"><p className="text-sm text-text-primary">{objResult.proof_point}</p></Panel>
                <Panel title="Pivot question" icon="fa-arrows-turn-right"><p className="text-sm text-text-primary">{objResult.pivot_question}</p></Panel>
                <Panel title="Do not do this" icon="fa-ban"><p className="text-sm text-text-primary">{objResult.avoid}</p></Panel>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'followup' && (
        <div className="space-y-4" data-testid="closer-followup-tab">
          <Panel title="Write the follow-up" icon="fa-pen-nib">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1.5">Channel</label>
                <select value={fuChannel} onChange={e => setFuChannel(e.target.value)} className="input-field w-36" data-testid="closer-fu-channel">
                  <option value="email">Email</option>
                  <option value="sms">Text</option>
                </select>
              </div>
              <div className="flex-1 min-w-[260px]">
                <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1.5">What happened last</label>
                <input value={fuContext} onChange={e => setFuContext(e.target.value)} placeholder="e.g. Ran the design, wife wasn't home, said call back Friday"
                  className="input-field w-full" data-testid="closer-fu-context" />
              </div>
              <button onClick={writeFollowUp} disabled={fuLoading} className="px-5 py-2.5 rounded-xl bg-text-primary text-surface text-sm font-bold disabled:opacity-60" data-testid="closer-fu-btn">
                {fuLoading ? <><i className="fa-solid fa-spinner fa-spin mr-2" />Writing…</> : <><i className="fa-solid fa-wand-magic-sparkles mr-2" />Draft it</>}
              </button>
            </div>
          </Panel>
          {fuResult && (
            <div className="p-5 rounded-2xl border border-border bg-surface" data-testid="closer-fu-result">
              <div className="flex items-center justify-between mb-3">
                <p className="text-[11px] font-bold uppercase tracking-widest text-text-muted">Send {fuResult.send_when}</p>
                <button onClick={() => copy(`${fuResult.subject ? fuResult.subject + '\n\n' : ''}${fuResult.body}`)} className="text-[11px] font-bold text-text-muted hover:text-primary"><i className="fa-solid fa-copy mr-1" />Copy</button>
              </div>
              {fuResult.subject && fuChannel === 'email' && <p className="font-black text-text-primary mb-2">{fuResult.subject}</p>}
              <p className="text-sm text-text-primary whitespace-pre-wrap">{fuResult.body}</p>
              <p className="mt-4 text-[11px] text-text-muted"><i className="fa-solid fa-lightbulb mr-1.5 text-amber-500" />{fuResult.why_this_works}</p>
            </div>
          )}
        </div>
      )}

      {tab === 'debrief' && (
        <div className="space-y-4" data-testid="closer-debrief-tab">
          <Panel title="Paste the call notes or transcript" icon="fa-microphone-lines">
            <textarea value={transcript} onChange={e => setTranscript(e.target.value)} rows={7}
              placeholder="Dump everything that was said — rough notes are fine."
              className="input-field w-full" data-testid="closer-transcript" />
            <div className="flex items-center justify-between mt-3">
              <label className="flex items-center gap-2 text-sm font-semibold text-text-primary cursor-pointer">
                <input type="checkbox" checked={applyStatus} onChange={e => setApplyStatus(e.target.checked)} className="w-4 h-4 accent-emerald-500" data-testid="closer-apply-status" />
                Let the AI move this lead's stage
              </label>
              <button onClick={runDebrief} disabled={debriefLoading} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 text-white text-sm font-bold disabled:opacity-60" data-testid="closer-debrief-btn">
                {debriefLoading ? <><i className="fa-solid fa-spinner fa-spin mr-2" />Reviewing…</> : <><i className="fa-solid fa-clipboard-check mr-2" />Debrief the call</>}
              </button>
            </div>
          </Panel>
          {debrief && (
            <div className="space-y-4" data-testid="closer-debrief-result">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: 'Temperature', value: debrief.buying_temperature, icon: 'fa-temperature-half' },
                  { label: 'Sentiment', value: debrief.sentiment, icon: 'fa-face-smile' },
                  { label: 'Close probability', value: `${debrief.close_probability ?? '—'}%`, icon: 'fa-percent' },
                  { label: 'Suggested stage', value: (debrief.suggested_status || '').replace('_', ' '), icon: 'fa-signs-post' },
                ].map(s => (
                  <div key={s.label} className="p-4 rounded-2xl border border-border bg-surface">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1"><i className={`fa-solid ${s.icon} mr-1.5`} />{s.label}</p>
                    <p className="text-lg font-black text-text-primary capitalize">{s.value}</p>
                  </div>
                ))}
              </div>
              <Panel title="Summary" icon="fa-file-lines"><p className="text-sm text-text-primary">{debrief.summary}</p></Panel>
              <div className="grid md:grid-cols-2 gap-4">
                <Panel title="Did well" icon="fa-thumbs-up">
                  <ul className="space-y-1">{(debrief.what_the_rep_did_well || []).map((x, i) => <li key={i} className="text-sm text-text-primary flex gap-2"><i className="fa-solid fa-check text-emerald-500 mt-1" />{x}</li>)}</ul>
                </Panel>
                <Panel title="Fix next time" icon="fa-screwdriver-wrench">
                  <ul className="space-y-1">{(debrief.what_to_fix || []).map((x, i) => <li key={i} className="text-sm text-text-primary flex gap-2"><i className="fa-solid fa-arrow-up text-amber-500 mt-1" />{x}</li>)}</ul>
                </Panel>
                <Panel title="Objections raised" icon="fa-shield">
                  <ul className="space-y-1">{(debrief.objections || []).map((x, i) => <li key={i} className="text-sm text-text-primary">“{x}”</li>)}</ul>
                </Panel>
                <Panel title="Next steps" icon="fa-list-check">
                  <ul className="space-y-1">{(debrief.next_steps || []).map((x, i) => <li key={i} className="text-sm text-text-primary flex justify-between gap-3"><span>{x.action}</span><span className="text-[11px] text-text-muted whitespace-nowrap">{x.when}</span></li>)}</ul>
                </Panel>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'coaching' && (
        <div className="space-y-4" data-testid="closer-coaching-tab">
          <div className="flex items-center justify-between">
            <p className="text-[11px] text-text-muted">Built from your real funnel — no self-reporting.</p>
            <button onClick={() => loadCoaching(true)} disabled={coachLoading} className="px-4 py-2 rounded-xl bg-text-primary text-surface text-sm font-bold disabled:opacity-60" data-testid="closer-coach-refresh">
              {coachLoading ? <><i className="fa-solid fa-spinner fa-spin mr-2" />Crunching…</> : <><i className="fa-solid fa-rotate mr-2" />Rebuild plan</>}
            </button>
          </div>
          {coach && coach.leads_worked <= 1 && coach.won === 0 && (
            <div className="px-4 py-3 rounded-xl bg-sky-50 border border-sky-200 text-sm text-sky-900" data-testid="closer-coach-empty">
              <i className="fa-solid fa-circle-info mr-2" />
              No worked leads on your name yet, so the percentages below start at zero. Claim leads from the
              <Link to="/leads" className="underline font-bold mx-1">Lead Database</Link>
              or let the <Link to="/sdr" className="underline font-bold">AI SDR</Link> work them, and this plan sharpens with every call.
            </div>
          )}
          {coach && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="closer-coach-rates">
                {Object.entries(coach.rates || {}).map(([key, val]) => {
                  const gap = coach.gaps?.[key] ?? 0;
                  const weak = coach.weakest_stage === key;
                  return (
                    <div key={key} className={`p-4 rounded-2xl border bg-surface ${weak ? 'border-rose-300' : 'border-border'}`}>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">{key.replace('_', ' ')}</p>
                      <p className="text-2xl font-black text-text-primary">{val}%</p>
                      <p className={`text-[11px] font-bold ${gap >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {gap >= 0 ? '+' : ''}{gap} vs benchmark {coach.benchmarks?.[key]}%
                      </p>
                      {weak && <p className="text-[10px] font-black uppercase text-rose-600 mt-1">Weakest link</p>}
                    </div>
                  );
                })}
              </div>
              <div className="p-6 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-transparent">
                <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">Verdict</p>
                <h2 className="text-lg font-black text-text-primary" data-testid="closer-coach-verdict">{coach.plan?.verdict}</h2>
                <p className="text-sm text-text-muted mt-2">{coach.plan?.weakest_stage_reason}</p>
                <div className="mt-4 px-4 py-3 rounded-xl bg-surface border border-border">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1">The one thing this week</p>
                  <p className="text-sm font-bold text-text-primary">{coach.plan?.one_thing}</p>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <Panel title="Drills" icon="fa-dumbbell" testid="closer-coach-drills">
                  <div className="space-y-3">
                    {(coach.plan?.drills || []).map((d, i) => (
                      <div key={i} className="p-3 rounded-xl bg-surface-alt border border-border">
                        <p className="text-sm font-black text-text-primary">{d.drill}</p>
                        <p className="text-[11px] text-text-muted">{d.why}</p>
                      </div>
                    ))}
                  </div>
                </Panel>
                <Panel title="Scripts to practice" icon="fa-scroll">
                  <ul className="space-y-1">{(coach.plan?.scripts_to_practice || []).map((s, i) => <li key={i} className="text-sm text-text-primary flex gap-2"><i className="fa-solid fa-angle-right text-primary mt-1" />{s}</li>)}</ul>
                </Panel>
                <Panel title="Targets" icon="fa-bullseye">
                  <ul className="space-y-1">{(coach.plan?.this_week_targets || []).map((s, i) => <li key={i} className="text-sm text-text-primary flex gap-2"><i className="fa-solid fa-crosshairs text-emerald-500 mt-1" />{s}</li>)}</ul>
                </Panel>
              </div>
            </>
          )}
          {board.length > 0 && (
            <div className="rounded-2xl border border-border bg-surface overflow-hidden" data-testid="closer-coach-board">
              <p className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-text-muted bg-surface-alt">Team weak-stage board</p>
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-widest text-text-muted">
                  <tr>
                    <th className="text-left px-5 py-2">Rep</th>
                    <th className="text-left px-3 py-2">Worked</th>
                    <th className="text-left px-3 py-2">Won</th>
                    <th className="text-left px-3 py-2">Contact</th>
                    <th className="text-left px-3 py-2">Qualify</th>
                    <th className="text-left px-3 py-2">Proposal</th>
                    <th className="text-left px-3 py-2">Close</th>
                    <th className="text-left px-5 py-2">Weakest</th>
                  </tr>
                </thead>
                <tbody>
                  {board.map(r => (
                    <tr key={r.email} className="border-t border-border">
                      <td className="px-5 py-2.5 font-bold text-text-primary">{r.name}</td>
                      <td className="px-3 py-2.5">{r.leads_worked}</td>
                      <td className="px-3 py-2.5 font-black text-emerald-600">{r.won}</td>
                      <td className="px-3 py-2.5">{r.rates.contact_rate}%</td>
                      <td className="px-3 py-2.5">{r.rates.qualify_rate}%</td>
                      <td className="px-3 py-2.5">{r.rates.proposal_rate}%</td>
                      <td className="px-3 py-2.5">{r.rates.close_rate}%</td>
                      <td className="px-5 py-2.5">
                        <span className="px-2 py-0.5 rounded-lg bg-rose-100 text-rose-700 text-[11px] font-bold">{r.weakest_stage.replace('_', ' ')} {r.gap}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
